// @ts-check
import dayjs from "dayjs";
import { SSE_ACTION, SSE_ENTITY } from "economos-shared/sse";
import { crewStore, economosStore, editedEntityStore, userStore } from "@/mobx/rootStore";
import {
	economosListQueries,
	economosMetricsQueries,
	economosMovementQueries,
} from "@/utils/useQuery/queries/queries";

/**
 * Evento SSE emitido por el backend (GET /events).
 * @typedef {Object} SseEvent
 * @property {string} type - "economosMovement" | "economosList" | "houseTask" | "houseItem" | "houseItemCategory"
 * @property {"created" | "updated" | "deleted"} action
 * @property {string} crewId
 * @property {string} [actorId]
 * @property {string} id
 * @property {Record<string, any> | null} data - toJson() de la entidad (null en deleted)
 */

/**
 * Orden idéntico al backend (ORDER BY date DESC).
 * @param {Record<string, any>} a
 * @param {Record<string, any>} b
 */
function sortByDateDesc(a, b) {
	return new Date(b.date).getTime() - new Date(a.date).getTime();
}

/**
 * @param {SseEvent} event
 */
function isForeignActor(event) {
	return Boolean(event.actorId) && event.actorId !== userStore.data?.id;
}

/**
 * @param {import("@tanstack/react-query").QueryClient} queryClient
 * @param {readonly unknown[]} key
 */
function invalidate(queryClient, key) {
	queryClient.invalidateQueries({
		queryKey: /** @type {readonly unknown[]} */ (key),
	});
}

/**
 * Aplica un evento SSE a la caché de TanStack Query de forma atómica (sin refetch):
 * inserta/reemplaza/elimina el elemento de las caches afectadas e invalida las
 * queries derivadas pequeñas (métricas, búsqueda, filtros, tags).
 * @param {import("@tanstack/react-query").QueryClient} queryClient
 * @param {SseEvent} event
 */
export function applySseEvent(queryClient, event) {
	if (!event?.type || !event?.id) return;

	if (event.type === SSE_ENTITY.ECONOMOS_MOVEMENT) {
		applyMovementEvent(queryClient, event);
	} else if (event.type === SSE_ENTITY.ECONOMOS_LIST) {
		applyListEvent(queryClient, event);
	} else if (event.type === SSE_ENTITY.HOUSE_TASK) {
		applyHouseTaskEvent(queryClient);
	} else if (event.type === SSE_ENTITY.HOUSE_ITEM) {
		applyHouseItemEvent(queryClient);
	} else if (event.type === SSE_ENTITY.HOUSE_ITEM_CATEGORY) {
		applyHouseItemCategoryEvent(queryClient);
	}

	if (
		(event.action === SSE_ACTION.UPDATED || event.action === SSE_ACTION.DELETED)
		&& isForeignActor(event)
	) {
		editedEntityStore.notifyForeignEdit({
			type: event.type,
			id: event.id,
			action: /** @type {"updated" | "deleted"} */ (event.action),
		});
	}
}

/**
 * @param {import("@tanstack/react-query").QueryClient} queryClient
 * @param {SseEvent} event
 */
function applyMovementEvent(queryClient, event) {
	const queryCache = queryClient.getQueryCache();
	const cachedQueries = queryCache.findAll({
		queryKey: economosMovementQueries.getMonth.key,
	});

	for (const cachedQuery of cachedQueries) {
		const [listId, year, month] = /** @type {string[]} */ (cachedQuery.queryKey.slice(2));
		const oldData = /** @type {{ data?: Record<string, any>[] } | undefined} */ (
			cachedQuery.state.data
		);
		const movements = oldData?.data;
		if (!Array.isArray(movements)) continue;

		const eventListId = event.data?.list;
		const eventDate = event.data?.date ? dayjs(event.data.date) : null;
		const belongsToCache =
			eventListId === listId
			&& eventDate?.year() === Number(year)
			&& (eventDate?.month() ?? -1) + 1 === Number(month);

		let nextMovements;

		if (event.action === SSE_ACTION.DELETED || !belongsToCache) {
			nextMovements = movements.filter((_movement) => _movement.id !== event.id);

			if (nextMovements.length === movements.length) continue;
		} else {
			nextMovements = [...movements.filter((_m) => _m.id !== event.id), event.data].sort(
				sortByDateDesc,
			);
		}

		queryClient.setQueryData(cachedQuery.queryKey, { ...oldData, data: nextMovements });
	}

	invalidate(queryClient, economosMetricsQueries.getMoneyMetrics.key);
	invalidate(queryClient, ["economosMovement", "getWithFilter"]);
	invalidate(queryClient, ["economosMovement", "getTags"]);
}

/**
 * @param {import("@tanstack/react-query").QueryClient} queryClient
 * @param {SseEvent} event
 */
function applyListEvent(queryClient, event) {
	const queryCache = queryClient.getQueryCache();
	const cachedQueries = queryCache.findAll({
		queryKey: economosListQueries.getAll.key,
	});

	for (const cachedQuery of cachedQueries) {
		const oldData = /** @type {{ data?: Record<string, any>[] } | undefined} */ (
			cachedQuery.state.data
		);
		const lists = oldData?.data;
		if (!Array.isArray(lists)) continue;

		let nextLists;

		if (event.action === SSE_ACTION.DELETED || !event.data) {
			nextLists = lists.filter((_list) => _list.id !== event.id);

			if (nextLists.length === lists.length) continue;
		} else {
			nextLists = [...lists.filter((_list) => _list.id !== event.id), event.data].sort(
				(a, b) => (a.order ?? 0) - (b.order ?? 0),
			);
		}

		queryClient.setQueryData(cachedQuery.queryKey, { ...oldData, data: nextLists });
	}

	if (event.action === "deleted" && economosStore.getSelectedListId() === event.id) {
		const crewId = crewStore.getSelectedCrew("id");
		const crewCache = cachedQueries.find((_query) => _query.queryKey.includes(crewId));
		const remaining =
			/** @type {{ data?: Record<string, any>[] }} */ (crewCache?.state.data)?.data ?? [];
		const lastRemaining = remaining[remaining.length - 1];

		if (lastRemaining?.id) {
			economosStore.setSelectedListId(lastRemaining.id);
		}
	}
}

/**
 * Las tareas llegan con métricas calculadas en servidor (deadlineAt, delayDays,
 * isDormant según el padre): invalidamos, sin patching local. El prefijo
 * ["houseTask"] cubre getAll, getAllCrews (dashboard multi-crew) y contribuciones.
 * @param {import("@tanstack/react-query").QueryClient} queryClient
 */
function applyHouseTaskEvent(queryClient) {
	invalidate(queryClient, ["houseTask"]);
}

/**
 * El listado de despensa se refresca por invalidación (el stock replenished
 * puede borrar el ítem y registrar compra: next/history cambian).
 * El prefijo ["houseItem"] cubre getAll y next.
 * @param {import("@tanstack/react-query").QueryClient} queryClient
 */
function applyHouseItemEvent(queryClient) {
	invalidate(queryClient, ["houseItem"]);
}

/**
 * @param {import("@tanstack/react-query").QueryClient} queryClient
 */
function applyHouseItemCategoryEvent(queryClient) {
	invalidate(queryClient, ["houseItemCategory"]);
}
